import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  getUsers,
  toggleUserStatus,
  updateUserRole,
} from "../../store/slices/adminSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Drawer from "../../components/UI/Drawer";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import { formatXRP } from "../../utils/formatters";
import { getCountryName } from "../../utils/countries";
import api from "../../services/api";

import {
  UsersIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  UserCircleIcon,
  Cog8ToothIcon,
  ShieldExclamationIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BanknotesIcon,
  UserPlusIcon,
  LinkIcon,
  XMarkIcon,
  TrophyIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserTransaction {
  id: number;
  type: string;
  amount: number;
  status: "pending" | "completed" | "rejected";
  tx_id?: string;
  tx_hash?: string;
  destination_address?: string;
  memo?: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

interface Referrer {
  id: number;
  username: string;
  email: string;
  referral_code?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid Date";
  }
};

const formatUserBalance = (balance: any): string => {
  if (balance === undefined || balance === null) return "0.00 XRP";
  try {
    const n =
      typeof balance === "string" ? parseFloat(balance) : Number(balance);
    return isNaN(n) ? "0.00 XRP" : `${formatXRP(n)} XRP`;
  } catch {
    return "0.00 XRP";
  }
};

const TX_TYPE_META: Record<
  string,
  { label: string; color: string; icon: React.ReactNode }
> = {
  deposit: {
    label: "Deposit",
    color: "text-green-400",
    icon: <ArrowDownIcon className="w-3.5 h-3.5" />,
  },
  withdrawal: {
    label: "Withdrawal",
    color: "text-red-400",
    icon: <ArrowUpIcon className="w-3.5 h-3.5" />,
  },
  trade_earning: {
    label: "Trade Earning",
    color: "text-blue-400",
    icon: <CurrencyDollarIcon className="w-3.5 h-3.5" />,
  },
  bot_earning: {
    label: "Bot Earning",
    color: "text-purple-400",
    icon: <CurrencyDollarIcon className="w-3.5 h-3.5" />,
  },
  airdrop: {
    label: "Airdrop",
    color: "text-yellow-400",
    icon: <BanknotesIcon className="w-3.5 h-3.5" />,
  },
  commission: {
    label: "Commission",
    color: "text-orange-400",
    icon: <BanknotesIcon className="w-3.5 h-3.5" />,
  },
  referral_earning: {
    label: "Referral",
    color: "text-teal-400",
    icon: <BanknotesIcon className="w-3.5 h-3.5" />,
  },
  signup_bonus: {
    label: "Signup Bonus",
    color: "text-pink-400",
    icon: <BanknotesIcon className="w-3.5 h-3.5" />,
  },
  signup_reward: {
    label: "Signup Reward",
    color: "text-pink-400",
    icon: <BanknotesIcon className="w-3.5 h-3.5" />,
  },
  bonus: {
    label: "Bonus",
    color: "text-emerald-400",
    icon: <BanknotesIcon className="w-3.5 h-3.5" />,
  },
  bot_purchase: {
    label: "Bot Purchase",
    color: "text-indigo-400",
    icon: <ArrowUpIcon className="w-3.5 h-3.5" />,
  },
  bot_capital_return: {
    label: "Capital Return",
    color: "text-teal-400",
    icon: <ArrowDownIcon className="w-3.5 h-3.5" />,
  },
  team_bonus: {
    label: "Team Bonus",
    color: "text-yellow-400",
    icon: <TrophyIcon className="w-3.5 h-3.5" />,
  },
};

const getTxMeta = (type: string) =>
  TX_TYPE_META[type] ?? {
    label: type,
    color: "text-gray-400",
    icon: <CurrencyDollarIcon className="w-3.5 h-3.5" />,
  };

const buildPageNumbers = (
  current: number,
  total: number,
): (number | "...")[] => {
  if (total <= 1) return [];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  pages.push(1);
  if (left > 2) pages.push("...");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("...");
  pages.push(total);
  return pages;
};

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  searchTerm: string;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, totalItems, searchTerm, onPageChange }) => {
  if (totalPages <= 1) return null;
  const pageNumbers = buildPageNumbers(currentPage, totalPages);
  const from = (currentPage - 1) * 20 + 1;
  const to = Math.min(currentPage * 20, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-6 border-t border-gray-800 gap-4">
      <p className={globalStyle.caption + " text-gray-400"}>
        Showing {from}–{to} of {totalItems}{" "}
        {searchTerm ? `results for "${searchTerm}"` : "users"} · Page{" "}
        {currentPage} of {totalPages}
      </p>
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="px-2"
        >
          <ChevronDoubleLeftIcon className="w-4 h-4" />
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Prev</span>
        </Button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((p, idx) =>
            p === "..." ? (
              <span
                key={`e-${idx}`}
                className="w-8 text-center text-gray-500 text-sm select-none"
              >
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p as number)}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === p
                    ? "bg-primary text-white border border-primary shadow-sm"
                    : "text-gray-400 hover:text-white hover:bg-gray-700 border border-transparent"
                }`}
              >
                {p}
              </button>
            ),
          )}
        </div>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2"
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRightIcon className="w-4 h-4" />
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2"
        >
          <ChevronDoubleRightIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── ReferredBySection ────────────────────────────────────────────────────────

const ReferredBySection: React.FC<{
  referrer: Referrer | null | undefined;
  onViewReferrer: (referrer: Referrer) => void;
}> = ({ referrer, onViewReferrer }) => {
  if (!referrer) {
    return (
      <div className="flex items-center gap-2 mt-1">
        <div className="w-7 h-7 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0">
          <UserPlusIcon className="w-3.5 h-3.5 text-gray-500" />
        </div>
        <span className="text-sm text-gray-500 italic">
          Not referred — signed up organically
        </span>
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-3 mt-1 p-2.5 rounded-lg border border-indigo-800/50 bg-indigo-900/20 cursor-pointer hover:bg-indigo-900/35 transition-colors group"
      onClick={() => onViewReferrer(referrer)}
      title={`Click to view ${referrer.username}'s details`}
    >
      <div className="w-8 h-8 rounded-full bg-indigo-900 border border-indigo-700 flex items-center justify-center flex-shrink-0 text-indigo-300 font-bold text-sm select-none">
        {referrer.username?.charAt(0).toUpperCase() ?? "?"}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-indigo-300 group-hover:text-indigo-200 transition-colors">
          @{referrer.username}
        </p>
        {referrer.email && (
          <p className="text-xs text-gray-500 truncate">{referrer.email}</p>
        )}
        {referrer.referral_code && (
          <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
            <LinkIcon className="w-3 h-3 flex-shrink-0" />
            Code: {referrer.referral_code}
          </p>
        )}
      </div>
      <span className="text-xs text-indigo-500 group-hover:text-indigo-400 flex-shrink-0 transition-colors">
        View →
      </span>
    </div>
  );
};

// ─── UserTransactionsModal ────────────────────────────────────────────────────

const UserTransactionsModal: React.FC<{
  user: any;
  isOpen: boolean;
  onClose: () => void;
}> = ({ user, isOpen, onClose }) => {
  const [transactions, setTransactions] = useState<UserTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchTransactions = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "15" });
      if (typeFilter !== "all") params.append("type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const res = await api.get(
        `/admin/users/${user.id}/transactions?${params.toString()}`,
      );
      if (Array.isArray(res.data)) {
        setTransactions(res.data);
        setTotalPages(1);
        setTotalItems(res.data.length);
      } else {
        setTransactions(res.data.data ?? []);
        setTotalPages(res.data.last_page ?? 1);
        setTotalItems(res.data.total ?? 0);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [user, page, typeFilter, statusFilter]);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchTransactions();
    }
  }, [isOpen, typeFilter, statusFilter, fetchTransactions]);
  useEffect(() => {
    if (isOpen) fetchTransactions();
  }, [page, fetchTransactions]);
  useEffect(() => {
    if (!isOpen) {
      setTransactions([]);
      setPage(1);
      setTypeFilter("all");
      setStatusFilter("all");
      setError(null);
    }
  }, [isOpen]);

  const summary = transactions.reduce(
    (acc, tx) => {
      const amt =
        typeof tx.amount === "string" ? parseFloat(tx.amount) : tx.amount;
      if (tx.status === "completed") acc.completed += isNaN(amt) ? 0 : amt;
      if (tx.status === "pending") acc.pending += isNaN(amt) ? 0 : amt;
      return acc;
    },
    { completed: 0, pending: 0 },
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Transactions — ${user?.username ?? ""}`}
      size="xl"
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-primary-900 flex items-center justify-center shrink-0">
            <UserIcon className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-white truncate">{user?.username}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
          <div className="ml-auto text-right shrink-0">
            <p className="text-xs text-gray-400">Balance</p>
            <p className="font-semibold text-white text-sm">
              {formatUserBalance(user?.xrp_balance)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-800 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">Total Shown</p>
            <p className="font-bold text-white">{totalItems}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">Completed</p>
            <p className="font-bold text-green-400">
              {formatXRP(summary.completed)} XRP
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400 mb-1">Pending</p>
            <p className="font-bold text-yellow-400">
              {formatXRP(summary.pending)} XRP
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">
              Type:
            </label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className={`${globalStyle.input} py-1 px-2 text-sm`}
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="trade_earning">Trade Earning</option>
              <option value="bot_earning">Bot Earning</option>
              <option value="airdrop">Airdrop</option>
              <option value="commission">Commission</option>
              <option value="referral_earning">Referral</option>
              <option value="signup_bonus">Signup Bonus</option>
              <option value="signup_reward">Signup Reward</option>
              <option value="bonus">Bonus</option>
              <option value="bot_purchase">Bot Purchase</option>
              <option value="bot_capital_return">Capital Return</option>
              <option value="team_bonus">Team Bonus</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">
              Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className={`${globalStyle.input} py-1 px-2 text-sm`}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <Button
            size="sm"
            variant="tertiary"
            onClick={fetchTransactions}
            loading={loading}
            className="ml-auto"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </Button>
        </div>

        {error && <Alert type="error" message={error} />}

        {loading ? (
          <div className="text-center py-10">
            <svg
              className={`${globalStyle.loadingSpinner} mx-auto`}
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm text-gray-400 mt-3">Loading transactions…</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-10">
            <CurrencyDollarIcon className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={globalStyle.table}>
              <thead className={globalStyle.tableHead}>
                <tr>
                  <th className={globalStyle.tableHeadCell}>Type</th>
                  <th className={globalStyle.tableHeadCell}>Amount</th>
                  <th className={globalStyle.tableHeadCell}>Status</th>
                  <th className={globalStyle.tableHeadCell}>Reference</th>
                  <th className={globalStyle.tableHeadCell}>Date</th>
                </tr>
              </thead>
              <tbody className={globalStyle.tableBody}>
                {transactions.map((tx) => {
                  const meta = getTxMeta(tx.type);
                  const amt =
                    typeof tx.amount === "string"
                      ? parseFloat(tx.amount)
                      : tx.amount;
                  const isCredit = [
                    "deposit",
                    "trade_earning",
                    "bot_earning",
                    "airdrop",
                    "commission",
                    "referral_earning",
                    "signup_bonus",
                    "signup_reward",
                    "bonus",
                    "bot_capital_return",
                    "team_bonus",
                  ].includes(tx.type);
                  return (
                    <tr key={tx.id} className={globalStyle.tableRowHover}>
                      <td className={globalStyle.tableCell}>
                        <div className="flex items-center gap-1.5">
                          <span className={meta.color}>{meta.icon}</span>
                          <span className="text-sm text-gray-300">
                            {meta.label}
                          </span>
                        </div>
                      </td>
                      <td className={globalStyle.tableCell}>
                        <span
                          className={`font-medium ${isCredit ? "text-green-400" : "text-red-400"}`}
                        >
                          {isCredit ? "+" : "-"}
                          {formatXRP(isNaN(amt) ? 0 : amt)} XRP
                        </span>
                      </td>
                      <td className={globalStyle.tableCell}>
                        <span
                          className={`${globalStyle.badge} ${tx.status === "completed" ? globalStyle.badgeSuccess : tx.status === "pending" ? globalStyle.badgeWarning : globalStyle.badgeDanger}`}
                        >
                          {tx.status.charAt(0).toUpperCase() +
                            tx.status.slice(1)}
                        </span>
                      </td>
                      <td className={globalStyle.tableCell}>
                        <div className="max-w-[160px]">
                          {tx.tx_hash ? (
                            <p
                              className="text-xs text-gray-400 truncate"
                              title={tx.tx_hash}
                            >
                              {tx.tx_hash.slice(0, 16)}…
                            </p>
                          ) : tx.tx_id ? (
                            <p
                              className="text-xs text-gray-400 truncate"
                              title={tx.tx_id}
                            >
                              {tx.tx_id}
                            </p>
                          ) : tx.destination_address ? (
                            <p
                              className="text-xs text-gray-400 truncate"
                              title={tx.destination_address}
                            >
                              → {tx.destination_address.slice(0, 14)}…
                            </p>
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </div>
                      </td>
                      <td className={globalStyle.tableCell}>
                        <span className="text-xs text-gray-400">
                          {formatDate(tx.created_at)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-800">
            <p className="text-xs text-gray-400">
              Page {page} of {totalPages} · {totalItems} total
            </p>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="tertiary"
                disabled={page === 1}
                onClick={() => setPage(1)}
                className="px-2"
              >
                <ChevronDoubleLeftIcon className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="tertiary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeftIcon className="w-4 h-4" /> Prev
              </Button>
              <Button
                size="sm"
                variant="tertiary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <ChevronRightIcon className="w-4 h-4 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="tertiary"
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
                className="px-2"
              >
                <ChevronDoubleRightIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-gray-800">
          <Button variant="tertiary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const AdminUsers: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    users,
    isLoading,
    error,
    totalPages,
    currentPage,
    totalItems,
    actionLoading,
  } = useSelector((state: RootState) => state.admin);
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showUserTxs, setShowUserTxs] = useState(false);
  const [txUser, setTxUser] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive" | "kyc_pending"
  >("all");
  const [roleFilter, setRoleFilter] = useState<"all" | "user" | "admin">("all");

  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    userId: number;
    action: "activate" | "deactivate";
  }>({ open: false, userId: 0, action: "activate" });
  const [roleChangeModal, setRoleChangeModal] = useState<{
    open: boolean;
    userId: number;
    currentRole: string;
    username: string;
  }>({ open: false, userId: 0, currentRole: "user", username: "" });

  const fetchUsers = useCallback(
    (page: number, search: string, status: string, role: string) => {
      const filters: any = { page, limit: 20 };
      if (status !== "all") filters.status = status;
      if (role !== "all") filters.role = role;
      if (search.trim()) filters.search = search.trim();
      dispatch(getUsers(filters));
    },
    [dispatch],
  );

  useEffect(() => {
    fetchUsers(1, "", "all", "all");
  }, [fetchUsers]);

  const prevStatusRef = useRef(statusFilter);
  const prevRoleRef = useRef(roleFilter);

  useEffect(() => {
    if (
      statusFilter !== prevStatusRef.current ||
      roleFilter !== prevRoleRef.current
    ) {
      prevStatusRef.current = statusFilter;
      prevRoleRef.current = roleFilter;
      fetchUsers(1, committedSearch, statusFilter, roleFilter);
    }
  }, [statusFilter, roleFilter, committedSearch, fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCommittedSearch(searchInput);
    fetchUsers(1, searchInput, statusFilter, roleFilter);
  };
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    fetchUsers(page, committedSearch, statusFilter, roleFilter);
  };
  const handleRefresh = () =>
    fetchUsers(currentPage, committedSearch, statusFilter, roleFilter);
  const handleClearAllFilters = () => {
    setSearchInput("");
    setCommittedSearch("");
    setStatusFilter("all");
    setRoleFilter("all");
    fetchUsers(1, "", "all", "all");
  };
  const handleClearSearch = () => {
    setSearchInput("");
    setCommittedSearch("");
    fetchUsers(1, "", statusFilter, roleFilter);
  };

  const handleToggleStatus = async (userId: number, isActive: boolean) => {
    const result = await dispatch(toggleUserStatus(userId));
    if (toggleUserStatus.fulfilled.match(result)) {
      setConfirmationModal({ open: false, userId: 0, action: "activate" });
      fetchUsers(currentPage, committedSearch, statusFilter, roleFilter);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    const result = await dispatch(updateUserRole({ userId, role: newRole }));
    if (updateUserRole.fulfilled.match(result)) {
      setRoleChangeModal({
        open: false,
        userId: 0,
        currentRole: "user",
        username: "",
      });
      if (selectedUser && selectedUser.id === userId)
        setSelectedUser({ ...selectedUser, role: newRole });
      fetchUsers(currentPage, committedSearch, statusFilter, roleFilter);
    }
  };

  const handleViewReferrer = useCallback(
    (referrer: Referrer) => {
      const fullUser = users.find((u) => u.id === referrer.id);
      if (fullUser) {
        setSelectedUser(fullUser);
      } else {
        setSelectedUser({
          id: referrer.id,
          username: referrer.username,
          email: referrer.email,
          referral_code: referrer.referral_code ?? null,
          first_name: null,
          last_name: null,
          xrp_balance: null,
          kyc_status: null,
          role: "user",
          is_active: true,
          email_verified_at: null,
          created_at: "",
          referred_by: null,
          referrer: null,
          total_referral_earnings: null,
        });
      }
    },
    [users],
  );

  const getKycBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeSuccess}`}>
            Approved
          </span>
        );
      case "pending":
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeWarning}`}>
            Pending
          </span>
        );
      case "rejected":
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeDanger}`}>
            Rejected
          </span>
        );
      default:
        return (
          <span className={`${globalStyle.badge} ${globalStyle.badgeInfo}`}>
            Not Submitted
          </span>
        );
    }
  };

  const getStatusBadge = (isActive: boolean) =>
    isActive ? (
      <span className={`${globalStyle.badge} ${globalStyle.badgeSuccess}`}>
        Active
      </span>
    ) : (
      <span className={`${globalStyle.badge} ${globalStyle.badgeDanger}`}>
        Inactive
      </span>
    );

  const getRoleBadge = (role: string) =>
    role === "admin" ? (
      <span className={`${globalStyle.badge} ${globalStyle.badgeDanger}`}>
        Admin
      </span>
    ) : (
      <span className={`${globalStyle.badge} ${globalStyle.badgeInfo}`}>
        User
      </span>
    );

  const getRoleDescription = (role: string) => {
    if (role === "admin") return "Full administrative access to all features";
    if (role === "user") return "Standard user permissions";
    return "";
  };

  const isCurrentUser = (userId: number) =>
    !!currentUser && currentUser.id === userId;

  const calculateTotalBalanceAmount = () => {
    if (!users || users.length === 0) return "0.00";
    try {
      const total = users.reduce((sum, user) => {
        const n =
          typeof user.xrp_balance === "string"
            ? parseFloat(user.xrp_balance)
            : Number(user.xrp_balance);
        return sum + (isNaN(n) ? 0 : n);
      }, 0);
      return formatXRP(total);
    } catch {
      return "0.00";
    }
  };

  const hasActiveFilters =
    committedSearch !== "" || statusFilter !== "all" || roleFilter !== "all";

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className={globalStyle.h3}>User Management</h1>
            <p className={globalStyle.body2 + " text-gray-400"}>
              Manage all platform users and their accounts
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button
              variant="secondary"
              onClick={handleRefresh}
              loading={isLoading}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2 text-primary" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={globalStyle.grid4}>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Total Users
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {totalItems || 0}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-900 flex items-center justify-center">
                <UsersIcon className="w-6 h-6 text-blue-300" />
              </div>
            </div>
          </Card>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Active Users
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {users ? users.filter((u) => u.is_active).length : 0}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-900 flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-green-300" />
              </div>
            </div>
          </Card>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Pending KYC
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {users
                    ? users.filter((u) => u.kyc_status === "pending").length
                    : 0}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-900 flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-yellow-300" />
              </div>
            </div>
          </Card>
          <Card padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <p className={globalStyle.caption + " text-gray-400"}>
                  Total Balance
                </p>
                <h3 className={globalStyle.h3 + " text-white"}>
                  {calculateTotalBalanceAmount()} XRP
                </h3>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-900 flex items-center justify-center">
                <CurrencyDollarIcon className="w-6 h-6 text-purple-300" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search & Filters */}
        <Card className="mt-6" padding="lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by username, email, or name..."
                  className={`${globalStyle.input} pl-10 pr-10 bg-gray-800 border-gray-700 text-white`}
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
            <div className="flex space-x-3">
              <Button variant="tertiary" onClick={() => setShowFilters(true)}>
                <FunnelIcon className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1.5 w-2 h-2 rounded-full bg-primary inline-block" />
                )}
              </Button>
              <Button
                type="submit"
                onClick={handleSearch as any}
                loading={isLoading}
              >
                Search
              </Button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-800">
              <span className="text-xs text-gray-500 self-center">
                Active filters:
              </span>
              {committedSearch && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-primary/20 text-primary border border-primary/30">
                  Search: "{committedSearch}"
                  <button
                    onClick={handleClearSearch}
                    className="hover:text-white transition-colors ml-0.5"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {statusFilter !== "all" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-blue-900/40 text-blue-300 border border-blue-800/40">
                  Status:{" "}
                  {statusFilter === "active"
                    ? "Active"
                    : statusFilter === "inactive"
                      ? "Inactive"
                      : "KYC Pending"}
                  <button
                    onClick={() => setStatusFilter("all")}
                    className="hover:text-white transition-colors ml-0.5"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              {roleFilter !== "all" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-purple-900/40 text-purple-300 border border-purple-800/40">
                  Role: {roleFilter === "admin" ? "Admin" : "User"}
                  <button
                    onClick={() => setRoleFilter("all")}
                    className="hover:text-white transition-colors ml-0.5"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </Card>

        {/* Users Table */}
        <Card className="mt-6" padding="lg">
          {error && <Alert type="error" message={error} className="mb-6" />}

          {isLoading ? (
            <div className="text-center py-12">
              <svg
                className={`${globalStyle.loadingSpinner} mx-auto`}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className={`${globalStyle.body2} mt-4 text-gray-400`}>
                Loading users...
              </p>
            </div>
          ) : !users || users.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className={globalStyle.h5}>No Users Found</h3>
              <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                {hasActiveFilters
                  ? "No results match your current filters"
                  : "No users in the system yet"}
              </p>
              {hasActiveFilters && (
                <Button
                  variant="tertiary"
                  size="sm"
                  className="mt-3"
                  onClick={handleClearAllFilters}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className={globalStyle.table}>
                  <thead className={globalStyle.tableHead}>
                    <tr>
                      <th className={globalStyle.tableHeadCell}>User</th>
                      <th className={globalStyle.tableHeadCell}>Email</th>
                      <th className={globalStyle.tableHeadCell}>Role</th>
                      <th className={globalStyle.tableHeadCell}>Balance</th>
                      <th className={globalStyle.tableHeadCell}>Country</th>
                      <th className={globalStyle.tableHeadCell}>KYC Status</th>
                      <th className={globalStyle.tableHeadCell}>Status</th>
                      <th className={globalStyle.tableHeadCell}>Joined</th>
                      <th className={globalStyle.tableHeadCell}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={globalStyle.tableBody}>
                    {users.map((user) => {
                      const isOwn = isCurrentUser(user.id);
                      return (
                        <tr key={user.id} className={globalStyle.tableRowHover}>
                          {/* User */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full bg-primary-900 flex items-center justify-center mr-3">
                                <UserIcon className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-white">
                                  {user.username || "N/A"}
                                  {isOwn && (
                                    <span className="ml-2 text-xs text-blue-400 font-normal">
                                      (You)
                                    </span>
                                  )}
                                </p>
                                <p
                                  className={
                                    globalStyle.caption + " text-gray-400"
                                  }
                                >
                                  {user.first_name || ""} {user.last_name || ""}
                                </p>
                              </div>
                            </div>
                          </td>
                          {/* Email */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex items-center">
                              <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="truncate max-w-[150px] text-gray-300">
                                {user.email || "N/A"}
                              </span>
                            </div>
                          </td>
                          {/* Role */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex items-center">
                              <UserCircleIcon className="w-4 h-4 text-gray-400 mr-2" />
                              {getRoleBadge(user.role || "user")}
                            </div>
                          </td>
                          {/* Balance */}
                          <td className={globalStyle.tableCell}>
                            <div className="font-medium text-white">
                              {formatUserBalance(user.xrp_balance)}
                            </div>
                          </td>
                          {/* Country */}
                          <td className={globalStyle.tableCell}>
                            {(user as any).country ? (
                              <div className="flex items-center gap-1.5">
                                <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span className="text-gray-300 text-sm truncate max-w-[110px]">
                                  {getCountryName((user as any).country)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-600 text-sm">—</span>
                            )}
                          </td>
                          {/* KYC */}
                          <td className={globalStyle.tableCell}>
                            {getKycBadge(user.kyc_status || "not_submitted")}
                          </td>
                          {/* Status */}
                          <td className={globalStyle.tableCell}>
                            {getStatusBadge(user.is_active)}
                          </td>
                          {/* Joined */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex items-center">
                              <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-gray-300">
                                {formatDate(user.created_at)}
                              </span>
                            </div>
                          </td>
                          {/* Actions */}
                          <td className={globalStyle.tableCell}>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="tertiary"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowUserDetails(true);
                                }}
                                title="View Details"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="tertiary"
                                onClick={() => {
                                  setTxUser(user);
                                  setShowUserTxs(true);
                                }}
                                title="View Transactions"
                              >
                                <CurrencyDollarIcon className="w-4 h-4 text-yellow-400" />
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() =>
                                  setRoleChangeModal({
                                    open: true,
                                    userId: user.id,
                                    currentRole: user.role || "user",
                                    username: user.username || "User",
                                  })
                                }
                                title={
                                  isOwn
                                    ? "Cannot change your own role"
                                    : "Change Role"
                                }
                                disabled={isOwn}
                              >
                                <Cog8ToothIcon className="w-4 h-4 text-primary" />
                              </Button>
                              <Button
                                size="sm"
                                variant={user.is_active ? "danger" : "success"}
                                onClick={() =>
                                  setConfirmationModal({
                                    open: true,
                                    userId: user.id,
                                    action: user.is_active
                                      ? "deactivate"
                                      : "activate",
                                  })
                                }
                                loading={
                                  actionLoading &&
                                  confirmationModal.userId === user.id &&
                                  confirmationModal.action ===
                                    (user.is_active ? "deactivate" : "activate")
                                }
                                title={
                                  isOwn
                                    ? "Cannot deactivate your own account"
                                    : user.is_active
                                      ? "Deactivate"
                                      : "Activate"
                                }
                                disabled={isOwn && user.is_active}
                              >
                                {user.is_active ? (
                                  <XCircleIcon className="w-4 h-4" />
                                ) : (
                                  <CheckCircleIcon className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                searchTerm={committedSearch}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card>
      </main>

      <BottomNav />

      {/* User Transactions Modal */}
      <UserTransactionsModal
        user={txUser}
        isOpen={showUserTxs}
        onClose={() => {
          setShowUserTxs(false);
          setTxUser(null);
        }}
      />

      {/* User Details Modal */}
      <Modal
        isOpen={showUserDetails}
        onClose={() => setShowUserDetails(false)}
        title="User Details"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-primary-900 flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className={globalStyle.h5}>
                  {selectedUser.username || "N/A"}
                  {isCurrentUser(selectedUser.id) && (
                    <span className="ml-2 text-sm text-blue-400 font-normal">
                      (Your Account)
                    </span>
                  )}
                </h3>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  {selectedUser.first_name || ""} {selectedUser.last_name || ""}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Email</h4>
                <div className="flex items-center mt-1">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <p className="text-white">{selectedUser.email || "N/A"}</p>
                </div>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Role</h4>
                <div className="flex items-center mt-1">
                  {getRoleBadge(selectedUser.role || "user")}
                  <span className="ml-2 text-sm text-gray-500">
                    {getRoleDescription(selectedUser.role || "user")}
                  </span>
                </div>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Balance</h4>
                <p className={`${globalStyle.h4} mt-1 text-white`}>
                  {formatUserBalance(selectedUser.xrp_balance)}
                </p>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  KYC Status
                </h4>
                <div className="mt-1">
                  {getKycBadge(selectedUser.kyc_status || "not_submitted")}
                </div>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  Account Status
                </h4>
                <div className="mt-1">
                  {getStatusBadge(selectedUser.is_active)}
                </div>
              </div>

              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Joined</h4>
                <div className="flex items-center mt-1">
                  <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <p className="text-white">
                    {formatDate(selectedUser.created_at)}
                  </p>
                </div>
              </div>

              {/* Country */}
              <div>
                <h4
                  className={`${globalStyle.h6} text-gray-400 flex items-center gap-1`}
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  Country
                </h4>
                <div className="flex items-center mt-1">
                  {selectedUser.country ? (
                    <p className="text-white">
                      {getCountryName(selectedUser.country)}
                    </p>
                  ) : (
                    <span className="text-gray-500 italic text-sm">
                      Not set
                    </span>
                  )}
                </div>
              </div>

              {/* Referred By */}
              <div className="md:col-span-2">
                <h4
                  className={`${globalStyle.h6} text-gray-400 flex items-center gap-1.5 mb-1`}
                >
                  <UserPlusIcon className="w-3.5 h-3.5" />
                  Referred By
                </h4>
                <ReferredBySection
                  referrer={selectedUser.referrer ?? null}
                  onViewReferrer={handleViewReferrer}
                />
              </div>

              {selectedUser.referral_code && (
                <div className="md:col-span-2">
                  <h4
                    className={`${globalStyle.h6} text-gray-400 flex items-center gap-1.5`}
                  >
                    <LinkIcon className="w-3.5 h-3.5" />
                    Their Referral Code
                  </h4>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="text-sm text-indigo-300 bg-indigo-900/30 border border-indigo-800/50 rounded px-2.5 py-1 font-mono">
                      {selectedUser.referral_code}
                    </code>
                    <span className="text-xs text-gray-500">
                      (share this to refer new users)
                    </span>
                  </div>
                </div>
              )}

              {selectedUser.total_referral_earnings !== undefined &&
                selectedUser.total_referral_earnings !== null &&
                parseFloat(selectedUser.total_referral_earnings) > 0 && (
                  <div>
                    <h4 className={`${globalStyle.h6} text-gray-400`}>
                      Referral Earnings
                    </h4>
                    <p className="text-teal-400 font-semibold mt-1">
                      {formatUserBalance(selectedUser.total_referral_earnings)}
                    </p>
                  </div>
                )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-800">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowUserDetails(false);
                  setTxUser(selectedUser);
                  setShowUserTxs(true);
                }}
              >
                <CurrencyDollarIcon className="w-4 h-4 mr-2 text-yellow-400" />
                View Transactions
              </Button>
              <Button
                variant="tertiary"
                onClick={() => setShowUserDetails(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Filters Drawer */}
      <Drawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filter Users"
        size="sm"
      >
        <div className="space-y-6">
          <div>
            <h4 className={`${globalStyle.h6} mb-3 text-white`}>
              Account Status
            </h4>
            <div className="space-y-2">
              {(["all", "active", "inactive"] as const).map((status) => (
                <label key={status} className="flex items-center">
                  <input
                    type="radio"
                    name="statusFilter"
                    checked={statusFilter === status}
                    onChange={() => setStatusFilter(status)}
                    className="w-4 h-4 text-primary border-gray-600 focus:ring-primary bg-gray-800"
                  />
                  <span className="ml-2 capitalize text-white">{status}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className={`${globalStyle.h6} mb-3 text-white`}>User Role</h4>
            <div className="space-y-2">
              {(["all", "user", "admin"] as const).map((role) => (
                <label key={role} className="flex items-center">
                  <input
                    type="radio"
                    name="roleFilter"
                    checked={roleFilter === role}
                    onChange={() => setRoleFilter(role)}
                    className="w-4 h-4 text-primary border-gray-600 focus:ring-primary bg-gray-800"
                  />
                  <span className="ml-2 capitalize text-white">{role}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handleClearAllFilters}
              variant="tertiary"
              fullWidth
            >
              Clear All Filters
            </Button>
            <Button onClick={() => setShowFilters(false)} fullWidth>
              Apply Filters
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmationModal.open}
        onClose={() =>
          setConfirmationModal({ open: false, userId: 0, action: "activate" })
        }
        title={
          confirmationModal.action === "activate"
            ? "Activate User"
            : "Deactivate User"
        }
        size="sm"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div
              className={`w-16 h-16 rounded-full mx-auto mb-4 ${confirmationModal.action === "activate" ? "bg-green-900" : "bg-red-900"}`}
            >
              {confirmationModal.action === "activate" ? (
                <CheckCircleIcon className="w-8 h-8 text-green-300 mx-auto mt-4" />
              ) : (
                <XCircleIcon className="w-8 h-8 text-red-300 mx-auto mt-4" />
              )}
            </div>
            <h3 className={globalStyle.h5}>
              {confirmationModal.action === "activate"
                ? "Activate User?"
                : "Deactivate User?"}
            </h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              {confirmationModal.action === "activate"
                ? "This user will be able to access their account."
                : "This user will no longer be able to access their account."}
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() =>
                setConfirmationModal({
                  open: false,
                  userId: 0,
                  action: "activate",
                })
              }
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmationModal.action === "activate" ? "success" : "danger"
              }
              onClick={() =>
                handleToggleStatus(
                  confirmationModal.userId,
                  confirmationModal.action === "deactivate",
                )
              }
              loading={actionLoading}
              fullWidth
            >
              {confirmationModal.action === "activate"
                ? "Activate"
                : "Deactivate"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Role Change Modal */}
      <Modal
        isOpen={roleChangeModal.open}
        onClose={() =>
          setRoleChangeModal({
            open: false,
            userId: 0,
            currentRole: "user",
            username: "",
          })
        }
        title="Change User Role"
        size="sm"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-purple-900 mx-auto mb-4">
              <ShieldExclamationIcon className="w-8 h-8 text-purple-300 mx-auto mt-4" />
            </div>
            <h3 className={globalStyle.h5}>
              Change Role for {roleChangeModal.username}
            </h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Current role:{" "}
              <span className="font-medium capitalize text-white">
                {roleChangeModal.currentRole}
              </span>
            </p>
          </div>
          <div className="space-y-3">
            {["user", "admin"].map((role) => (
              <button
                key={role}
                onClick={() => handleRoleChange(roleChangeModal.userId, role)}
                disabled={roleChangeModal.currentRole === role || actionLoading}
                className={`w-full p-3 rounded-lg border text-left transition-colors ${
                  roleChangeModal.currentRole === role
                    ? "border-primary bg-primary-900 text-primary"
                    : "border-gray-700 hover:bg-gray-800 text-white"
                } ${actionLoading || roleChangeModal.currentRole === role ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium capitalize">{role}</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {getRoleDescription(role)}
                    </p>
                  </div>
                  {roleChangeModal.currentRole === role && (
                    <CheckCircleIcon className="w-5 h-5 text-primary" />
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className="pt-4 border-t border-gray-800">
            <Button
              variant="tertiary"
              onClick={() =>
                setRoleChangeModal({
                  open: false,
                  userId: 0,
                  currentRole: "user",
                  username: "",
                })
              }
              fullWidth
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminUsers;
